import { useState, useEffect, useMemo } from 'react';
import { BREAKPOINTS, type Breakpoint } from '@/constants/breakpoints';

export interface BreakpointState {
  /** Screen width < 768px */
  isMobile: boolean;
  /** Screen width 768px - 1023px */
  isTablet: boolean;
  /** Screen width >= 1024px */
  isDesktop: boolean;
  /** Current breakpoint name based on screen width */
  breakpoint: Breakpoint;
  /** Raw breakpoint values for reference */
  breakpoints: typeof BREAKPOINTS;
}

/**
 * Calculates breakpoint state from window width.
 * Uses a single width value to derive all breakpoint flags efficiently.
 */
function calculateBreakpointState(width: number): Omit<BreakpointState, 'breakpoints'> {
  // Semantic flags
  const isMobile = width < BREAKPOINTS.md;
  const isTablet = width >= BREAKPOINTS.md && width < BREAKPOINTS.lg;
  const isDesktop = width >= BREAKPOINTS.lg;

  // Exact breakpoint detection
  let breakpoint: Breakpoint;
  if (width >= BREAKPOINTS['2xl']) {
    breakpoint = '2xl';
  } else if (width >= BREAKPOINTS.xl) {
    breakpoint = 'xl';
  } else if (width >= BREAKPOINTS.lg) {
    breakpoint = 'lg';
  } else if (width >= BREAKPOINTS.md) {
    breakpoint = 'md';
  } else if (width >= BREAKPOINTS.sm) {
    breakpoint = 'sm';
  } else {
    breakpoint = 'xs';
  }

  return { isMobile, isTablet, isDesktop, breakpoint };
}

/**
 * Hook that provides semantic breakpoint detection.
 * Uses Tailwind's breakpoints: mobile for < 768px (xs/sm), tablet for 768-1023px (md),
 * and desktop for >= 1024px (lg).
 *
 * Uses a single resize listener for efficiency instead of multiple media query listeners.
 *
 * @returns Object with boolean flags and current breakpoint name
 *
 * @example
 * const { isMobile, isDesktop } = useBreakpoint();
 *
 * return (
 *   <div className={isMobile ? 'p-2' : 'p-6'}>
 *     {isMobile ? <MobileNav /> : <DesktopNav />}
 *   </div>
 * );
 */
export function useBreakpoint(): BreakpointState {
  const [state, setState] = useState<Omit<BreakpointState, 'breakpoints'>>(() => {
    // SSR-safe initial state
    if (typeof window === 'undefined') {
      return { isMobile: false, isTablet: false, isDesktop: true, breakpoint: 'lg' as Breakpoint };
    }
    return calculateBreakpointState(window.innerWidth);
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const handleResize = () => {
      const newState = calculateBreakpointState(window.innerWidth);
      setState(prev => {
        // Only update if values actually changed to prevent unnecessary re-renders
        if (
          prev.isMobile !== newState.isMobile ||
          prev.isTablet !== newState.isTablet ||
          prev.isDesktop !== newState.isDesktop ||
          prev.breakpoint !== newState.breakpoint
        ) {
          return newState;
        }
        return prev;
      });
    };

    const debouncedHandleResize = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(handleResize, 150);
    };

    window.addEventListener('resize', debouncedHandleResize);
    return () => {
      window.removeEventListener('resize', debouncedHandleResize);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  return useMemo(() => ({
    ...state,
    breakpoints: BREAKPOINTS,
  }), [state]);
}
